import { useCallback, useState } from 'react'
import { getTokenPayload } from '@/lib/auth'

const STORAGE_PREFIX = 'tp_sidebar_collapsed:'

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`
}

function readInitial(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

export interface CollapsedGroupsApi {
  isCollapsed: (groupId: string) => boolean
  toggle: (groupId: string) => void
}

/**
 * Persiste por usuário (escopo via `sub` do JWT) quais grupos da sidebar
 * estão colapsados. Cai pra `_anon` se não houver token (não deve acontecer
 * dentro do AppShell, que está atrás de ProtectedRoute).
 */
export function useCollapsedGroups(): CollapsedGroupsApi {
  const userId = getTokenPayload()?.sub ?? '_anon'
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readInitial(userId))

  const toggle = useCallback(
    (groupId: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev)
        if (next.has(groupId)) next.delete(groupId)
        else next.add(groupId)
        try {
          localStorage.setItem(storageKey(userId), JSON.stringify([...next]))
        } catch {
          // quota cheia / modo privado — manter estado em memória mesmo assim
        }
        return next
      })
    },
    [userId],
  )

  const isCollapsed = useCallback((groupId: string) => collapsed.has(groupId), [collapsed])

  return { isCollapsed, toggle }
}
